import { useCallback } from 'react';
// @ts-ignore
import inflection from 'inflection';
import { parse } from 'query-string';
import { useLocation } from 'react-router-dom';
import { useCheckMinimumRequiredProps } from '../checkMinimumRequiredProps';
import { useCreate } from '../../dataProvider';
import { useNotify, useRedirect, } from '../../sideEffect';
import { useSaveModifiers, } from '../saveModifiers';
import { useTranslate } from '../../i18n';
import useVersion from '../useVersion';
import { CRUD_CREATE } from '../../actions';
import { useResourceContext } from '../../core';
/**
 * Prepare data for the Create view
 *
 * @param {Object} props The props passed to the Create component.
 *
 * @return {Object} controllerProps Fetched data and callbacks for the Create view
 *
 * @example
 *
 * import { useCreateController } from 'react-admin';
 * import CreateView from './CreateView';
 *
 * const MyCreate = props => {
 *     const controllerProps = useCreateController(props);
 *     return <CreateView {...controllerProps} {...props} />;
 * }
 */
export var useCreateController = function (props) {
    useCheckMinimumRequiredProps('Create', ['basePath', 'resource'], props);
    var basePath = props.basePath, hasEdit = props.hasEdit, hasShow = props.hasShow, _a = props.record, record = _a === void 0 ? {} : _a, successMessage = props.successMessage, onSuccess = props.onSuccess, onFailure = props.onFailure, transform = props.transform;
    var resource = useResourceContext(props);
    var location = useLocation();
    var translate = useTranslate();
    var notify = useNotify();
    var redirect = useRedirect();
    var recordToUse = getRecord(location, record);
    var version = useVersion();
    if (process.env.NODE_ENV !== 'production' && successMessage) {
        console.log('<Create successMessage> prop is deprecated, use the onSuccess prop instead.');
    }
    var _b = useSaveModifiers({ onSuccess: onSuccess, onFailure: onFailure, transform: transform }), onSuccessRef = _b.onSuccessRef, setOnSuccess = _b.setOnSuccess, onFailureRef = _b.onFailureRef, setOnFailure = _b.setOnFailure, transformRef = _b.transformRef, setTransform = _b.setTransform;
    var _c = useCreate(resource), create = _c[0], saving = _c[1].loading;
    var save = useCallback(function (data, redirectTo, _a) {
        if (redirectTo === void 0) { redirectTo = 'list'; }
        var _b = _a === void 0 ? {} : _a, onSuccessFromSave = _b.onSuccess, onFailureFromSave = _b.onFailure, transformFromSave = _b.transform;
        return Promise.resolve(transformFromSave
            ? transformFromSave(data)
            : transformRef.current
                ? transformRef.current(data)
                : data).then(function (data) {
            return create({ payload: { data: data } }, {
                action: CRUD_CREATE,
                onSuccess: onSuccessFromSave
                    ? onSuccessFromSave
                    : onSuccessRef.current
                        ? onSuccessRef.current
                        : function (_a) {
                            var newRecord = _a.data;
                            notify(successMessage ||
                                'ra.notification.created', 'info', {
                                smart_count: 1,
                            });
                            redirect(redirectTo, basePath, newRecord.id, newRecord);
                        },
                onFailure: onFailureFromSave
                    ? onFailureFromSave
                    : onFailureRef.current
                        ? onFailureRef.current
                        : function (error) {
                            notify(typeof error === 'string'
                                ? error
                                : error.message ||
                                    'ra.notification.http_error', 'warning', {
                                _: typeof error === 'string'
                                    ? error
                                    : error && error.message
                                        ? error.message
                                        : undefined,
                            });
                        },
            });
        });
    }, [
        transformRef,
        create,
        onSuccessRef,
        onFailureRef,
        notify,
        successMessage,
        redirect,
        basePath,
    ]);
    var resourceName = translate("resources." + resource + ".name", {
        smart_count: 1,
        _: inflection.humanize(inflection.singularize(resource)),
    });
    var defaultTitle = translate('ra.page.create', {
        name: "" + resourceName,
    });
    return {
        loading: false,
        loaded: true,
        saving: saving,
        defaultTitle: defaultTitle,
        onFailureRef: onFailureRef,
        onSuccessRef: onSuccessRef,
        transformRef: transformRef,
        save: save,
        setOnSuccess: setOnSuccess,
        setOnFailure: setOnFailure,
        setTransform: setTransform,
        resource: resource,
        basePath: basePath,
        record: recordToUse,
        redirect: getDefaultRedirectRoute(hasShow, hasEdit),
        version: version,
    };
};
export var getRecord = function (_a, record) {
    var state = _a.state, search = _a.search;
    if (record === void 0) { record = {}; }
    if (state && state.record) {
        return state.record;
    }
    if (search) {
        try {
            var searchParams = parse(search);
            if (searchParams.source) {
                if (Array.isArray(searchParams.source)) {
                    console.error("Failed to parse location search parameter '" + search + "'. To pre-fill some fields in the Create form, pass a stringified source parameter (e.g. '?source={\"title\":\"foo\"}')");
                    return;
                }
                return JSON.parse(searchParams.source);
            }
        }
        catch (e) {
            console.error("Failed to parse location search parameter '" + search + "'. To pre-fill some fields in the Create form, pass a stringified source parameter (e.g. '?source={\"title\":\"foo\"}')");
        }
    }
    return record;
};
var getDefaultRedirectRoute = function (hasShow, hasEdit) {
    if (hasEdit) {
        return 'edit';
    }
    if (hasShow) {
        return 'show';
    }
    return 'list';
};
